import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  DashboardSelectionContext,
  DashboardSortDirection,
  DashboardWidgetDrilldownResponse,
  DashboardWidgetDrilldownRow
} from 'src/app/core-module/models/Dashboards/dashboard.models';

@Component({
  selector: 'app-dashboard-drilldown',
  templateUrl: './dashboard-drilldown.component.html',
  styleUrl: './dashboard-drilldown.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DashboardDrilldownComponent {
  @Input() drilldown: DashboardWidgetDrilldownResponse | null = null;
  @Input() loading = false;
  @Input() sortField: keyof DashboardWidgetDrilldownRow = 'DateDisplay';
  @Input() sortDirection: DashboardSortDirection = 'desc';

  @Output() closePanel = new EventEmitter<void>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() sortChanged = new EventEmitter<keyof DashboardWidgetDrilldownRow>();
  @Output() selectionOpened = new EventEmitter<DashboardSelectionContext>();

  close(): void {
    this.closePanel.emit();
  }

  previousPage(): void {
    if (!this.drilldown || this.drilldown.PageNumber <= 1) {
      return;
    }

    this.pageChanged.emit(this.drilldown.PageNumber - 1);
  }

  nextPage(): void {
    if (!this.drilldown || this.drilldown.PageNumber >= this.totalPages()) {
      return;
    }

    this.pageChanged.emit(this.drilldown.PageNumber + 1);
  }

  sortBy(field: keyof DashboardWidgetDrilldownRow): void {
    this.sortChanged.emit(field);
  }

  totalPages(): number {
    if (!this.drilldown || this.drilldown.TotalRows === 0) {
      return 1;
    }

    return Math.ceil(this.drilldown.TotalRows / this.drilldown.PageSize);
  }

  rowStatusClass(row: DashboardWidgetDrilldownRow): string {
    return `row-status-${row.Status}`;
  }

  hasError(): boolean {
    return !!this.drilldown?.IsError;
  }

  isEmpty(): boolean {
    return !!this.drilldown && !this.hasError() && this.drilldown.TotalRows === 0;
  }

  rowTypeLabel(row: DashboardWidgetDrilldownRow): string {
    if (row.RowType === 'auditRequirement') {
      return 'Audit requirement';
    }
    if (row.RowType === 'pillar') {
      return 'Quality pillar';
    }
    return '';
  }

  canOpenRecord(row: DashboardWidgetDrilldownRow): boolean {
    return !!row.ActionLabel && (!!row.Selection || !!this.recordLinkUrl(row));
  }

  openSelection(row: DashboardWidgetDrilldownRow): void {
    if (!row.Selection) {
      return;
    }

    this.selectionOpened.emit(row.Selection);
  }

  showStatusColumn(): boolean {
    if (!this.drilldown || this.drilldown.Rows.length === 0) {
      return true;
    }

    return !this.drilldown.Rows.every(row => row.RowType === 'auditResult') || this.isQualityDrilldown();
  }

  isOfficeRanking(): boolean {
    return !!this.drilldown?.Rows.length
      && this.drilldown.Rows.every(row => row.RowType === 'office');
  }

  showScoreColumn(): boolean {
    if (!this.drilldown || this.drilldown.Rows.length === 0) {
      return false;
    }

    return this.drilldown.Rows.every(row => row.RowType === 'auditResult') || this.isQualityDrilldown();
  }

  scoreDisplay(row: DashboardWidgetDrilldownRow): string {
    const score = this.scoreValue(row);
    return score === null ? '-' : `${Math.round(score)}%`;
  }

  recordDetailText(row: DashboardWidgetDrilldownRow): string {
    if (this.isOfficeRanking()) {
      return row.SecondaryText;
    }
    if (!this.showScoreColumn()) {
      return row.SecondaryText;
    }

    const detail = row.SecondaryText
      .replace(/^Score\s+\d+(?:\.\d+)?%\s*-\s*/i, '')
      .replace(/^Score\s+\d+(?:\.\d+)?%$/i, '')
      .trim();
    return [detail, row.Frequency, row.Coverage].filter(value => !!value).join(' - ');
  }

  recordMetaText(row: DashboardWidgetDrilldownRow): string {
    if (this.isOfficeRanking()) {
      return '';
    }
    if (!this.showScoreColumn()) {
      return '';
    }

    const current = [row.LocationName, row.DateDisplay].filter(value => !!value).join(' - ');
    const previous = row.PreviousScore === undefined || row.PreviousScore === null
      ? ''
      : `Previous: ${Math.round(row.PreviousScore)}%${row.PreviousDateDisplay ? ` (${row.PreviousDateDisplay})` : ''}`;
    return [current, previous].filter(value => !!value).join(' · ');
  }

  previousScoreDisplay(row: DashboardWidgetDrilldownRow): string {
    return typeof row.PreviousScore === 'number' && Number.isFinite(row.PreviousScore)
      ? `${Math.round(row.PreviousScore)}%`
      : '-';
  }

  movementDisplay(row: DashboardWidgetDrilldownRow): string {
    if (typeof row.Movement !== 'number' || !Number.isFinite(row.Movement)) {
      return '-';
    }
    const roundedMovement = Math.round(row.Movement);
    return `${roundedMovement > 0 ? '+' : ''}${roundedMovement} pp`;
  }

  recordLinkUrl(row: DashboardWidgetDrilldownRow): string {
    if (row.RowType === 'auditResult') {
      return `/results?auditResultId=${row.RowID}&from=dashboard`;
    }

    return row.LinkUrl;
  }

  sortMarker(field: keyof DashboardWidgetDrilldownRow): string {
    if (this.sortField !== field) {
      return '';
    }

    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  private scoreValue(row: DashboardWidgetDrilldownRow): number | null {
    if (typeof row.Score === 'number' && Number.isFinite(row.Score)) {
      return row.Score;
    }

    const match = /score\s+(\d+(?:\.\d+)?)%/i.exec(row.SecondaryText || '');
    if (!match) {
      return null;
    }

    const score = Number(match[1]);
    return Number.isFinite(score) ? score : null;
  }

  private isQualityDrilldown(): boolean {
    return !!this.drilldown?.Rows.some(row => !!row.PillarName || !!row.Frequency || !!row.Coverage || row.PreviousScore !== undefined);
  }
}
